import { useState } from 'react';
import { Link } from 'react-router-dom';

import { DataTable, Pager } from '@/components/DataTable';
import { moment } from '@/lib/dates';
import {
  TICKETS_PER_PAGE,
  TICKET_KINDS,
  TICKET_STATUSES,
  statusLabel,
  useTickets,
  type TicketRow,
  type TicketStatus,
} from '@/lib/tickets';

/**
 * The reports people sent from the website.
 *
 * A mailbox rather than a catalogue, so it is sorted by when the
 * conversation last moved rather than by when a report arrived — a bug
 * somebody wrote in about this morning matters more than one answered last
 * week — and the filter defaults to the inbox: everything except the spam
 * shelf, which is only ever shown when it is asked for by name.
 */

const GRID = 'minmax(0, 2.2fr) 1fr 0.9fr 0.9fr 110px';

/** The colour of a status, by what it asks of the studio. */
const statusColour = (status: TicketStatus): string => {
  if (status === 'NEW') return 'var(--accent)';
  if (status === 'OPEN') return 'var(--warn)';
  if (status === 'SPAM') return 'var(--dim)';
  return 'var(--muted)';
};

function TableNotice({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 px-[22px] py-10 text-center text-[13.5px]" style={{ color: 'var(--muted)' }}>
      {children}
    </p>
  );
}

function TicketLine({ ticket }: { ticket: TicketRow }) {
  return (
    <div
      className="grid items-center border-b"
      style={{ gridTemplateColumns: '1fr', borderColor: 'var(--line)' }}
    >
      <Link
        to={`/tickets/${ticket.id}`}
        className="row-link focus-ring grid items-center gap-3 py-2.5 pl-3.5"
        style={{ gridTemplateColumns: GRID }}
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            {/*
              A dot for a report nobody has answered. The status says what
              was decided about it; this says whether anything was said back,
              which is the one thing somebody scanning the list is looking
              for.
            */}
            {ticket.replied ? null : (
              <>
                {/*
                  The word, not only the dot: `aria-label` on a `<span>` with
                  no role is ignored by the accessibility tree, so a screen
                  reader met a row that simply said nothing about this.
                */}
                <span className="sr-only">Not answered yet</span>
                <span
                  aria-hidden="true"
                  title="Not answered yet"
                  className="h-[6px] w-[6px] flex-none rounded-full"
                  style={{ background: 'var(--accent)' }}
                />
              </>
            )}
            <span className="truncate text-[13.5px] font-medium">{ticket.subject}</span>
          </span>
          <span className="truncate text-[11.5px]" style={{ color: 'var(--dim)' }}>
            <span className="font-mono">{ticket.reference}</span>
            {' · '}
            {ticket.name ? `${ticket.name} · ` : ''}
            {ticket.email}
          </span>
        </span>

        <span className="truncate text-[12.5px]" style={{ color: 'var(--muted)' }}>
          {/* An app that has since been deleted leaves the report behind. */}
          {ticket.appName ?? '—'}
        </span>

        <span className="text-[12.5px]" style={{ color: 'var(--muted)' }}>
          {TICKET_KINDS[ticket.kind] ?? ticket.kind}
        </span>

        <span className="text-[12px]" style={{ color: statusColour(ticket.status) }}>
          {statusLabel(ticket.status)}
        </span>

        <span
          className="pr-3.5 text-right text-[11.5px] whitespace-nowrap"
          style={{ color: 'var(--dim)' }}
        >
          {moment(ticket.lastMessageAt)}
        </span>
      </Link>
    </div>
  );
}

export function TicketsPage() {
  /*
   * Null is the inbox — every status except spam — and it is the default
   * because that is what somebody opening this screen came to read. The
   * shelf is one choice away and never mixed in.
   */
  const [status, setStatus] = useState<TicketStatus | null>(null);
  const [page, setPage] = useState(1);
  const tickets = useTickets({ status, page });

  const choose = (next: TicketStatus | null) => {
    setStatus(next);
    // Page four of one filter is not page four of another.
    setPage(1);
  };

  const total = tickets.data?.total ?? 0;
  /*
   * ⚠️ Put the page back when the list shrinks under it. Reports move
   * between filters as they are answered and shelved, so the inbox page
   * somebody is standing on can stop existing while they read it — and an
   * out-of-range offset answers an empty table, under the sentence "No
   * reports yet", with the pager gone and no way back except reloading.
   * The same clamp the other two lists carry, for the same reason.
   */
  const pages = Math.max(1, Math.ceil(total / TICKETS_PER_PAGE));
  if (tickets.data && page > pages) setPage(pages);
  /*
   * The waiting count is the *inbox's*, not this filter's — the API counts
   * every report that is new or open, whichever page is being looked at. So
   * it is only printed beside the inbox's own total; next to "Closed" it
   * would read as a count of closed reports waiting, which is nonsense.
   *
   * ⚠️ And it counts something different from the dot on a row. The dot is
   * "nobody has written back"; this is "nobody has finished with it" — a
   * report answered and deliberately left open is in one and not the other.
   * They are worded apart on purpose, because one number called the same
   * name as another and disagreeing with it is worse than two numbers.
   */
  const waiting = status === null ? (tickets.data?.unanswered ?? 0) : 0;
  const meta = tickets.data
    ? `${total} ${total === 1 ? 'report' : 'reports'}${
        waiting > 0 ? ` · ${waiting} waiting on us` : ''
      }`
    : '';

  return (
    <section className="flex flex-col gap-3.5 px-[18px] pt-3.5 pb-6">
      {/*
        No search box: what a person looks for here is a status or a code,
        and the API has no search over reports yet. `TableToolbar` takes one
        or it does not appear — so the filter stands alone.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
          {[{ value: null, label: 'Inbox' }, ...TICKET_STATUSES].map((one) => {
            const on = status === one.value;
            return (
              <button
                key={one.label}
                type="button"
                onClick={() => choose(one.value as TicketStatus | null)}
                aria-pressed={on}
                className="focus-ring min-h-8 cursor-pointer rounded-full border px-3.5 text-[12.5px]"
                style={{
                  borderColor: on ? 'var(--accent)' : 'var(--line2)',
                  background: on ? 'var(--accent-soft)' : 'var(--panel2)',
                  color: on ? 'var(--accent)' : 'var(--text)',
                }}
              >
                {one.label}
              </button>
            );
          })}
        </div>
        <span className="text-[12px]" style={{ color: 'var(--dim)' }} aria-live="polite">
          {meta}
        </span>
      </div>

      <DataTable columns={['Report', 'App', 'Kind', 'Status', 'Last message']} gridTemplate={GRID}>
        {tickets.isPending ? (
          <TableNotice>Loading…</TableNotice>
        ) : tickets.isError ? (
          <TableNotice>Could not load the reports. Reload the page to try again.</TableNotice>
        ) : tickets.data.items.length === 0 ? (
          <TableNotice>
            {status === 'SPAM'
              ? 'Nothing on the spam shelf.'
              : status
                ? `No reports are ${statusLabel(status).toLowerCase()}.`
                : 'No reports yet. They arrive from the form on the website.'}
          </TableNotice>
        ) : (
          tickets.data.items.map((ticket) => <TicketLine key={ticket.id} ticket={ticket} />)
        )}
      </DataTable>

      <Pager page={page} perPage={TICKETS_PER_PAGE} total={total} onPage={setPage} noun="report" />
    </section>
  );
}

export default TicketsPage;
